import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { 
  ArrowLeft,
  Building2,
  MapPin,
  Calendar,
  Users,
  Clock,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertTriangle,
  FileText,
  User,
  Star,
  MessageSquare,
  Send,
  ArrowUpDown,
  ThumbsUp,
  ThumbsDown,
  TrendingUp,
  Phone,
  Mail,
  Eye,
  MessageCircle,
  X,
  Flag,
  Upload,
  Image as ImageIcon
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ClientProjectDetailV3() {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token, user } = useAuth();
  
  // Get initial tab from URL params
  const initialTab = searchParams.get("tab") || "details";
  
  const [loading, setLoading] = useState(true);
  const [project, setProject] = useState(null);
  const [selecting, setSelecting] = useState(false);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showSelectDialog, setShowSelectDialog] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Chat state (with Holding)
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  
  // Team chat state
  const [teamMessages, setTeamMessages] = useState([]);
  const [newTeamMessage, setNewTeamMessage] = useState("");
  const [sendingTeamMessage, setSendingTeamMessage] = useState(false);
  const teamMessagesEndRef = useRef(null);
  const teamChatContainerRef = useRef(null);
  
  // Daily progress state
  const [dailyProgress, setDailyProgress] = useState([]);
  
  // Counter-offer state
  const [counterOffer, setCounterOffer] = useState(null);
  const [respondingToOffer, setRespondingToOffer] = useState(false);
  
  // Team contract status state
  const [teamContractStatus, setTeamContractStatus] = useState(null);
  const [contractSubTab, setContractSubTab] = useState("holding");
  
  // View Signed Contract modal state
  const [showSignedContractModal, setShowSignedContractModal] = useState(false);
  const [signedContractData, setSignedContractData] = useState(null);
  const [loadingSignatures, setLoadingSignatures] = useState(false);

  // End Project state
  const [showEndProjectModal, setShowEndProjectModal] = useState(false);
  const [endProjectNote, setEndProjectNote] = useState("");
  const [endProjectFiles, setEndProjectFiles] = useState([]);
  const [uploadingEndFile, setUploadingEndFile] = useState(false);
  const [endingProject, setEndingProject] = useState(false);
  const [completionStatus, setCompletionStatus] = useState(null);

  // Auto-scroll to bottom of CHAT CONTAINER only (not whole page)
  const scrollToBottom = () => {
    if (activeTab === "details" && chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [chatMessages, activeTab]);

  useEffect(() => {
    fetchProject();
  }, [projectId, token]);

  // Mark project-related notifications as read when page loads
  useEffect(() => {
    if (projectId && token) {
      fetch(`${API_URL}/api/notifications/mark-read-for-project/${projectId}`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}` }
      }).catch(() => {});
    }
  }, [projectId, token]);

  // Polling for new messages every 5 seconds
  useEffect(() => {
    const pollInterval = setInterval(() => {
      fetchMessagesOnly();
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [projectId, token]);

  // Polling for project status changes every 10 seconds (for Holding approval)
  useEffect(() => {
    const statusPollInterval = setInterval(() => {
      if (project && ["pending_holding_review", "pending_approval"].includes(project.status)) {
        // Silently refresh project to check for status changes
        fetchProject(true); // silent refresh
      }
    }, 10000);

    return () => clearInterval(statusPollInterval);
  }, [projectId, token, project?.status]);

  useEffect(() => {
    if (project) {
      // Load chat messages from project
      setChatMessages(project.negotiation_messages || []);
      
      // Check for counter-offer
      if (project.counter_offer && project.counter_offer.status === "pending") {
        setCounterOffer(project.counter_offer);
      }
    }
  }, [project]);

  // Fetch only messages without full reload
  const fetchMessagesOnly = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/browse/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        // Only update messages if they changed
        if (JSON.stringify(data.negotiation_messages) !== JSON.stringify(chatMessages)) {
          setChatMessages(data.negotiation_messages || []);
        }
        // Check for new counter-offer
        if (data.counter_offer && data.counter_offer.status === "pending" && !counterOffer) {
          setCounterOffer(data.counter_offer);
        }
      }
    } catch (error) {
      // Silent fail for polling
    }
  };

  const fetchProject = async (silent = false) => {
    try {
      if (!silent) setLoading(true);
      const res = await fetch(`${API_URL}/api/projects-v3/browse/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) throw new Error("Project not found");
      
      const data = await res.json();
      
      // Check if status changed (for notifications)
      if (project && project.status !== data.status) {
        // Status changed! Show notification
        if (data.status === "approved_pending_client_sign") {
          toast.success("Prosjektet er godkjent av Holding! Du kan nå signere kontrakten.");
        }
      }
      
      setProject(data);
      
      // Fetch team contract status if team is selected (check all possible indicators)
      const hasTeam = data.team_leader_id || 
                      ["team_selected", "team_contracted", "in_progress", "completed", "pending_completion"].includes(data.status) ||
                      data.applications?.some(app => app.status === "selected");
      if (hasTeam) {
        fetchTeamContractStatus();
      }
      
      // Fetch completion status if project is in completion phase
      if (["pending_completion", "completed"].includes(data.status)) {
        fetchCompletionStatus();
      }
      
    } catch (error) {
      if (!silent) {
        console.error("Error fetching project:", error);
        toast.error("Kunne ikke laste prosjekt");
        navigate("/client/projects");
      }
    } finally {
      if (!silent) setLoading(false);
    }
  };
  
  const fetchTeamContractStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/client/team-contract-status/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTeamContractStatus(data);
      }
    } catch (error) {
      console.error("Error fetching team contract status:", error);
    }
  };

  // Fetch signed contract data (signatures)
  const fetchSignedContract = async () => {
    setLoadingSignatures(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/client/signed-contract/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setSignedContractData(data);
        setShowSignedContractModal(true);
      } else {
        toast.error(t('common.error'));
      }
    } catch (error) {
      console.error("Error fetching signed contract:", error);
      toast.error(t('common.error'));
    } finally {
      setLoadingSignatures(false);
    }
  };

  // Fetch completion status
  const fetchCompletionStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/completion-status/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setCompletionStatus(data);
      }
    } catch (error) {
      console.error("Error fetching completion status:", error);
    }
  };

  // Handle file upload for end project
  const handleEndProjectFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploadingEndFile(true);
    try {
      for (const file of files) {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("upload_preset", "xstillas_uploads");

        const res = await axios.post(
          "https://api.cloudinary.com/v1_1/dzljsey7j/auto/upload",
          formData
        );

        setEndProjectFiles(prev => [...prev, {
          url: res.data.secure_url,
          name: file.name,
          type: file.type
        }]);
      }
      toast.success(t('common.uploadSuccess') || 'Fil lastet opp');
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(t('common.uploadError') || 'Opplasting feilet');
    } finally {
      setUploadingEndFile(false);
    }
  };

  // Handle end project submit
  const handleEndProject = async () => {
    setEndingProject(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/client/end-project/${projectId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          completion_note: endProjectNote,
          completion_files: endProjectFiles
        })
      });

      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Kunne ikke avslutte prosjekt");
      }

      const data = await res.json();
      toast.success(data.message);
      setShowEndProjectModal(false);
      fetchProject();
      fetchCompletionStatus();
    } catch (error) {
      console.error("End project error:", error);
      toast.error(error.message);
    } finally {
      setEndingProject(false);
    }
  };

  // Authenticated PDF download/view
  const handlePdfAction = async (url, filename, action = 'download') => {
    try {
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        throw new Error('Kunne ikke hente PDF');
      }
      
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      if (action === 'view') {
        window.open(blobUrl, '_blank');
      } else {
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      
      // Cleanup after a delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
    } catch (error) {
      console.error('PDF action error:', error);
      toast.error(error.message);
    }
  };

  const handleSelectTeam = async () => {
    if (!selectedApp) return;
    
    setSelecting(true);
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/select-team/${projectId}/${selectedApp.application_id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || "Kunne ikke velge team");
      }
      
      toast.success("Team valgt!");
      setShowSelectDialog(false);
      fetchProject();
      
    } catch (error) {
      console.error("Error selecting team:", error);
      toast.error(error.message);
    } finally {
      setSelecting(false);
    }
  };

  // Send chat message to Holding
  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      const res = await axios.post(
        `${API_URL}/api/projects-v3/client/message/${projectId}`,
        { message: newMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatMessages(prev => [...prev, res.data.message]);
      setNewMessage("");
      toast.success("Melding sendt");
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Kunne ikke sende melding");
    } finally {
      setSendingMessage(false);
    }
  };

  // Respond to counter-offer
  const handleCounterOfferResponse = async (accept) => {
    setRespondingToOffer(true);
    try {
      await axios.post(
        `${API_URL}/api/projects-v3/client/counter-offer-response/${projectId}`,
        { accept },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (accept) {
        toast.success("Mot-tilbud akseptert!");
      } else {
        toast.info("Mot-tilbud avslått");
      }
      setCounterOffer(null);
      fetchProject();
    } catch (error) {
      console.error("Error responding to counter-offer:", error);
      toast.error("Kunne ikke svare på mot-tilbud");
    } finally {
      setRespondingToOffer(false);
    }
  };

  // Fetch team messages (when team is assigned)
  const fetchTeamMessages = async () => {
    // Check for team in multiple places (V3 structure)
    const hasTeam = project?.team_leader_id || 
                    project?.selected_team?.team_leader_id || 
                    project?.status === "team_selected" || 
                    project?.status === "in_progress" ||
                    project?.status === "team_contracted";
    
    if (!hasTeam) return;
    
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/chat/project/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setTeamMessages(data.messages || []);
      }
    } catch (error) {
      console.error("Error fetching team messages:", error);
    }
  };

  // Send message to team
  const handleSendTeamMessage = async (e) => {
    e.preventDefault();
    if (!newTeamMessage.trim()) return;
    
    setSendingTeamMessage(true);
    try {
      await fetch(`${API_URL}/api/projects-v3/chat/project/${projectId}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          message: newTeamMessage,
          sender_type: "client"
        })
      });
      
      setNewTeamMessage("");
      fetchTeamMessages();
      toast.success("Melding sendt til team");
    } catch (error) {
      console.error("Error sending team message:", error);
      toast.error("Kunne ikke sende melding");
    } finally {
      setSendingTeamMessage(false);
    }
  };

  // Fetch daily progress
  const fetchDailyProgress = async () => {
    if (project?.status !== "in_progress" && project?.status !== "completed") return;
    
    try {
      const res = await fetch(`${API_URL}/api/projects-v3/daily-progress/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        setDailyProgress(data.progress || []);
      }
    } catch (error) {
      console.error("Error fetching daily progress:", error);
    }
  };

  // Poll for team messages and daily progress
  useEffect(() => {
    const hasTeam = project?.team_leader_id || 
                    project?.selected_team?.team_leader_id || 
                    project?.status === "team_selected" || 
                    project?.status === "in_progress" ||
                    project?.status === "team_contracted";
    
    if (activeTab === "contact" && hasTeam) {
      fetchTeamMessages();
      const interval = setInterval(fetchTeamMessages, 5000);
      return () => clearInterval(interval);
    }
  }, [activeTab, project]);

  useEffect(() => {
    if (activeTab === "progress") {
      fetchDailyProgress();
    }
  }, [activeTab, project]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("no-NO", {
      style: "currency",
      currency: "NOK",
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("no-NO", {
      day: "2-digit",
      month: "long",
      year: "numeric"
    });
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending_holding_review: { label: t('projectStatus.pendingReview'), color: "bg-yellow-500/20 text-yellow-400" },
      approved_pending_client_sign: { label: t('projectStatus.pendingSignature'), color: "bg-blue-500/20 text-blue-400" },
      live: { label: t('projectStatus.seekingTeam'), color: "bg-green-500/20 text-green-400" },
      has_applications: { label: t('projectStatus.hasApplications'), color: "bg-purple-500/20 text-purple-400" },
      team_selected: { label: t('projectStatus.teamSelected'), color: "bg-cyan-500/20 text-cyan-400" },
      team_contracted: { label: t('projectStatus.contractSigned'), color: "bg-emerald-500/20 text-emerald-400" },
      in_progress: { label: t('projectStatus.inProgress'), color: "bg-emerald-500/20 text-emerald-400" },
      pending_completion: { label: t('projectStatus.pendingCompletion') || 'Venter på signaturer', color: "bg-orange-500/20 text-orange-400" },
      completed: { label: t('projectStatus.completed'), color: "bg-green-500/20 text-green-400" },
      rejected: { label: t('projectStatus.rejected'), color: "bg-red-500/20 text-red-400" }
    };
    
    const config = configs[status] || { label: status, color: "bg-gray-500/20 text-gray-400" };
    return <Badge className={`${config.color} border-0`}>{config.label}</Badge>;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-12 h-12 border-2 border-[#23F7DD] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!project) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto text-yellow-400 mb-4" />
          <h2 className="text-white text-xl">Prosjekt ikke funnet</h2>
        </div>
      </DashboardLayout>
    );
  }

  const applications = project.applications || [];
  const hasApplications = applications.length > 0;
  const canSelectTeam = ["live", "has_applications"].includes(project.status) && hasApplications;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-6" data-testid="client-project-detail-v3">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/client/projects")}
            className="text-gray-400 hover:text-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-white">{project.title}</h1>
              {getStatusBadge(project.status)}
            </div>
            <p className="text-gray-400 mt-1">Opprettet {formatDate(project.created_at)}</p>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#1a1a2e] border-b border-[#2a2a4a]">
            <TabsTrigger value="details" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              {t('projectDetail.details')}
            </TabsTrigger>
            <TabsTrigger value="contract" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
              <FileText className="w-4 h-4 mr-2" />
              {t('projectDetail.contract')}
            </TabsTrigger>
            <TabsTrigger value="applications" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              {t('projectDetail.applications')}
              {hasApplications && <Badge className="ml-2 bg-indigo-600">{applications.length}</Badge>}
            </TabsTrigger>
            {["team_selected", "team_contracted", "in_progress", "completed"].includes(project.status) && (
              <TabsTrigger value="progress" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
                <TrendingUp className="w-4 h-4 mr-2" />
                {t('projectDetail.dailyProgress')}
              </TabsTrigger>
            )}
            {(project.team_leader_id || project.status === "team_selected" || project.status === "team_contracted" || project.status === "in_progress") && (
              <TabsTrigger value="contact" className="data-[state=active]:bg-indigo-500 data-[state=active]:text-white">
                <MessageCircle className="w-4 h-4 mr-2" />
                {t('projectDetail.chatTeam') || 'Chat Team'}
              </TabsTrigger>
            )}
          </TabsList>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            {/* Main Content - Will contain tab content */}
            <div className="lg:col-span-2">
              {/* Details Tab */}
              <TabsContent value="details" className="space-y-6 mt-0">
                {/* Project Details */}
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">{t('projectDetail.title')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3">
                    <MapPin className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">{t('projectDetail.location')}</p>
                      <p className="text-white">{project.location_city}</p>
                      <p className="text-gray-500 text-xs">{project.location_address}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Users className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">{t('projectDetail.teamSize')}</p>
                      <p className="text-white">{project.crew_size} {t('common.persons')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Clock className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">{t('projectDetail.duration')}</p>
                      <p className="text-white">{project.estimated_duration_days} {t('common.days')}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Calendar className="w-5 h-5 text-gray-400" />
                    <div>
                      <p className="text-gray-400 text-sm">{t('projectDetail.startDate')}</p>
                      <p className="text-white">{formatDate(project.start_date)}</p>
                    </div>
                  </div>
                </div>
                
                <div className="pt-4 border-t border-[#2a2a4a]">
                  <p className="text-gray-400 text-sm mb-2">{t('projectDetail.description')}</p>
                  <p className="text-white whitespace-pre-wrap">{project.description}</p>
                </div>
              </CardContent>
            </Card>

                {/* Counter-Offer Section - IN DETAILS TAB */}
                {counterOffer && (
                  <Card className="bg-[#1a1a2e] border-orange-500/50 border-2">
                    <CardHeader className="bg-orange-500/10">
                      <CardTitle className="text-white flex items-center gap-2">
                        <ArrowUpDown className="w-5 h-5 text-orange-400" />
                        Mot-tilbud fra AllStillas Holding
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4 pt-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-4 bg-[#0f0f1a] rounded-lg">
                          <p className="text-gray-400 text-sm mb-1">Ditt budsjett</p>
                          <p className="text-white font-bold text-lg line-through opacity-50">
                            {formatCurrency(project.proposed_budget)}
                          </p>
                        </div>
                        <div className="p-4 bg-orange-500/10 rounded-lg border border-orange-500/30">
                          <p className="text-orange-400 text-sm mb-1">Foreslått budsjett</p>
                          <p className="text-orange-400 font-bold text-lg">
                            {formatCurrency(counterOffer.proposed_budget)}
                          </p>
                        </div>
                      </div>
                      
                      {counterOffer.message && (
                        <div className="p-4 bg-[#0f0f1a] rounded-lg">
                          <p className="text-gray-400 text-sm mb-2">Melding fra Holding:</p>
                          <p className="text-white">{counterOffer.message}</p>
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-2">
                        <Button 
                          onClick={() => handleCounterOfferResponse(true)}
                          disabled={respondingToOffer}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                        >
                          <ThumbsUp className="w-4 h-4 mr-2" />
                          Aksepter Tilbud
                        </Button>
                        <Button 
                          onClick={() => handleCounterOfferResponse(false)}
                          disabled={respondingToOffer}
                          variant="outline"
                          className="flex-1 border-red-500/50 text-red-400 hover:bg-red-500/10"
                        >
                          <ThumbsDown className="w-4 h-4 mr-2" />
                          Avslå
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Chat with Holding - IN DETAILS TAB */}
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageSquare className="w-5 h-5 text-blue-400" />
                      {t('projectDetail.communicationWithHolding')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Messages */}
                    <div 
                      ref={chatContainerRef}
                      className="h-64 overflow-y-auto space-y-3 p-3 bg-[#0f0f1a] rounded-lg"
                    >
                      {chatMessages.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-center">
                          <div>
                            <MessageSquare className="w-10 h-10 mx-auto text-gray-600 mb-2" />
                            <p className="text-gray-500 text-sm">{t('projectDetail.noMessages')}</p>
                            <p className="text-gray-600 text-xs mt-1">{t('projectDetail.startConversation')}</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {chatMessages.map((msg, idx) => (
                            <div 
                              key={idx} 
                              className={`flex ${msg.sender_type === "client" || msg.from === "client" ? "justify-end" : "justify-start"}`}
                            >
                              <div className={`max-w-[80%] p-3 rounded-lg ${
                                msg.sender_type === "client" || msg.from === "client"
                                  ? "bg-[#23F7DD]/20 text-[#23F7DD]" 
                                  : "bg-blue-500/20 text-blue-300"
                              }`}>
                                <p className="text-xs opacity-70 mb-1">
                                  {msg.sender_type === "client" || msg.from === "client" ? t('common.you') : "AllStillas Holding"}
                                </p>
                                <p className="text-white text-sm">{msg.message}</p>
                                <p className="text-xs opacity-50 mt-1">
                                  {new Date(msg.sent_at || msg.timestamp).toLocaleString("no-NO", {
                                    day: "2-digit",
                                    month: "short",
                                    hour: "2-digit",
                                    minute: "2-digit"
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                          <div ref={messagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Message Input */}
                    <div className="flex gap-2">
                      <Textarea
                        placeholder={t('projectDetail.writeMessage')}
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white resize-none"
                        rows={2}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage();
                          }
                        }}
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="bg-blue-600 hover:bg-blue-700 text-white px-4"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Contract Tab */}
              <TabsContent value="contract" className="space-y-6 mt-0">
                {/* Sub-tabs for contract types */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={contractSubTab === "holding" ? "default" : "outline"}
                    onClick={() => setContractSubTab("holding")}
                    className={contractSubTab === "holding" 
                      ? "bg-blue-600 text-white" 
                      : "border-[#2a2a4a] text-gray-400 hover:text-white"}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('projectDetail.contractWithHolding') || 'Kontrakt med Holding'}
                  </Button>
                  {(project.team_leader_id || ["team_selected", "team_contracted", "in_progress", "completed"].includes(project.status) || project.applications?.some(app => app.status === "selected")) && (
                    <Button
                      variant={contractSubTab === "team" ? "default" : "outline"}
                      onClick={() => setContractSubTab("team")}
                      className={contractSubTab === "team" 
                        ? "bg-purple-600 text-white" 
                        : "border-[#2a2a4a] text-gray-400 hover:text-white"}
                    >
                      <Users className="w-4 h-4 mr-2" />
                      {t('projectDetail.contractWithTeam') || 'Kontrakt med Team'}
                      {teamContractStatus && (
                        <Badge className={`ml-2 ${teamContractStatus.all_members_signed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                          {teamContractStatus.signed_count}/{teamContractStatus.total_members}
                        </Badge>
                      )}
                    </Button>
                  )}
                </div>

                {/* Holding Contract Sub-tab */}
                {contractSubTab === "holding" && (
                  <>
                    {/* Platform Contract (Admin & Client) */}
                    <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <FileText className="w-5 h-5 text-blue-400" />
                          {t('projectDetail.platformContract')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {project.client_signed ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                              <div className="flex items-center gap-2 text-green-400 mb-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-semibold">{t('projectDetail.contractSigned')}</span>
                              </div>
                              <p className="text-gray-400 text-sm">
                                {t('projectDetail.youSignedOn')} {project.client_signed_at ? formatDate(project.client_signed_at) : ''}
                              </p>
                            </div>
                            
                            {/* Contract Details */}
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-[#0f0f1a] rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">{t('projectDetail.signedBy')}</p>
                                <p className="text-white font-medium">{project.client_name || user?.name || 'Client'}</p>
                              </div>
                              <div className="p-3 bg-[#0f0f1a] rounded-lg">
                                <p className="text-gray-400 text-xs mb-1">{t('projectDetail.signedDate')}</p>
                                <p className="text-white font-medium">{project.client_signed_at ? formatDate(project.client_signed_at) : '-'}</p>
                              </div>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button 
                                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                                onClick={() => handlePdfAction(
                                  `${API_URL}/api/contracts-pdf/client-holding/${projectId}`,
                                  `contract-holding-${projectId}.pdf`,
                                  'view'
                                )}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                {t('projectDetail.viewContract') || 'Se Kontrakt'}
                              </Button>
                              <Button 
                                className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                                onClick={() => handlePdfAction(
                                  `${API_URL}/api/contracts-pdf/client-holding/${projectId}`,
                                  `contract-holding-${projectId}.pdf`,
                                  'download'
                                )}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                {t('projectDetail.downloadContract') || 'Last ned'}
                              </Button>
                            </div>
                          </div>
                        ) : project.status === "approved_pending_client_sign" ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                              <div className="flex items-center gap-2 text-blue-400 mb-2">
                                <FileText className="w-5 h-5" />
                                <span className="font-semibold">{t('projectDetail.contractReady') || 'Kontrakten er klar'}</span>
                              </div>
                              <p className="text-gray-400 text-sm">
                                {t('projectDetail.pleaseSignContract') || 'Vennligst signer kontrakten for å publisere prosjektet.'}
                              </p>
                            </div>
                            
                            {/* Contract Preview */}
                            <div className="p-4 bg-[#0f0f1a] rounded-lg space-y-2">
                              <div className="flex justify-between">
                                <span className="text-gray-400">{t('projectDetail.totalBudget')}</span>
                                <span className="text-white font-bold">{formatCurrency(project.final_budget)}</span>
                              </div>
                              {project.payment_terms_days && (
                                <div className="flex justify-between">
                                  <span className="text-gray-400">{t('projectDetail.paymentTerms')}</span>
                                  <span className="text-white">{project.payment_terms_days} {t('common.days')}</span>
                                </div>
                              )}
                            </div>
                            
                            <Button 
                              className="w-full bg-green-600 hover:bg-green-700 text-white"
                              onClick={() => navigate(`/client/projects-v3/${projectId}/sign`)}
                            >
                              <CheckCircle className="w-4 h-4 mr-2" />
                              {t('projectDetail.signContract') || 'Signer Kontrakt'}
                            </Button>
                          </div>
                        ) : (
                          <div className="text-center py-6">
                            <FileText className="w-10 h-10 mx-auto text-gray-600 mb-3" />
                            <p className="text-gray-400">{t('projectDetail.waitingForSignature')}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Financial Breakdown */}
                    {project.final_budget && (
                      <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <DollarSign className="w-5 h-5 text-green-400" />
                            {t('projectDetail.financialOverview')}
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="flex justify-between items-center p-3 bg-[#0f0f1a] rounded-lg">
                            <span className="text-gray-400">{t('projectDetail.totalBudget')}</span>
                            <span className="text-white font-bold text-lg">{formatCurrency(project.final_budget)}</span>
                          </div>
                          {project.payment_terms_days && (
                            <div className="flex justify-between items-center p-3 bg-[#0f0f1a] rounded-lg">
                              <span className="text-gray-400">{t('projectDetail.paymentTerms')}</span>
                              <span className="text-white">{project.payment_terms_days} {t('common.days')}</span>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}

                    {/* Contract Terms */}
                    <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <CardHeader>
                        <CardTitle className="text-white">{t('projectDetail.contractTerms')}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2 text-gray-300 text-sm">
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{t('projectDetail.contractTerm1', { budget: formatCurrency(project.final_budget || project.proposed_budget) })}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{t('projectDetail.contractTerm2', { days: project.estimated_duration_days || 14 })}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{t('projectDetail.contractTerm3')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{t('projectDetail.contractTerm4')}</span>
                          </li>
                          <li className="flex items-start gap-2">
                            <CheckCircle className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                            <span>{t('projectDetail.contractTerm5', { days: project.payment_terms_days || 14 })}</span>
                          </li>
                        </ul>
                      </CardContent>
                    </Card>
                  </>
                )}

                {/* Team Contract Sub-tab */}
                {contractSubTab === "team" && (project.team_leader_id || ["team_selected", "team_contracted", "in_progress", "completed"].includes(project.status) || project.applications?.some(app => app.status === "selected")) && (
                  <>
                    {/* Team Contract Status */}
                    <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                      <CardHeader>
                        <CardTitle className="text-white flex items-center gap-2">
                          <Users className="w-5 h-5 text-purple-400" />
                          {t('projectDetail.teamContract')}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {(project.team_contract_signed || project.team_contract_signed_at || (teamContractStatus && teamContractStatus.all_members_signed)) ? (
                          <div className="space-y-4">
                            <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                              <div className="flex items-center gap-2 text-green-400 mb-2">
                                <CheckCircle className="w-5 h-5" />
                                <span className="font-semibold">{t('projectDetail.teamContractSigned')}</span>
                              </div>
                              <p className="text-gray-400 text-sm">
                                {t('projectDetail.signedByTeam')} {project.team_contract_signed_at ? formatDate(project.team_contract_signed_at) : ''}
                              </p>
                            </div>
                            
                            <div className="flex gap-2">
                              <Button 
                                className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                                onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/team/${projectId}`, `team-contract-${projectId}.pdf`, 'download')}
                              >
                                <FileText className="w-4 h-4 mr-2" />
                                {t('projectDetail.downloadTeamContract')}
                              </Button>
                              <Button 
                                className="flex-1 bg-indigo-600 text-white hover:bg-indigo-700"
                                onClick={fetchSignedContract}
                                disabled={loadingSignatures}
                              >
                                <Eye className="w-4 h-4 mr-2" />
                                {t('projectDetail.viewSignatures') || 'Se Signaturer'}
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                            <div className="flex items-center gap-2 text-yellow-400 mb-2">
                              <Clock className="w-5 h-5" />
                              <span className="font-semibold">{t('projectDetail.waitingForTeamSignature')}</span>
                            </div>
                            <p className="text-gray-400 text-sm">
                              {t('projectDetail.teamMustSign')}
                            </p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Team Signature Status */}
                    {teamContractStatus && teamContractStatus.has_team && (
                      <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                        <CardHeader>
                          <CardTitle className="text-white flex items-center gap-2">
                            <FileText className="w-5 h-5 text-indigo-400" />
                            {t('projectDetail.signatureStatus') || 'Signaturstatus'}
                            <Badge className={`ml-2 ${teamContractStatus.all_members_signed ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                              {teamContractStatus.signed_count}/{teamContractStatus.total_members} {t('projectDetail.signed') || 'signert'}
                            </Badge>
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            {teamContractStatus.members.map((member, idx) => (
                              <div 
                                key={idx}
                                className={`p-4 rounded-lg border ${
                                  member.has_signed 
                                    ? 'bg-green-500/5 border-green-500/30' 
                                    : 'bg-[#0f0f1a] border-[#2a2a4a]'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <div className="flex items-center gap-3">
                                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                      member.role === 'Basleder' || member.role === 'Team Leader'
                                        ? 'bg-purple-500/20 text-purple-400'
                                        : 'bg-blue-500/20 text-blue-400'
                                    }`}>
                                      <User className="w-5 h-5" />
                                    </div>
                                    <div>
                                      <p className="text-white font-medium">{member.name}</p>
                                      <div className="flex items-center gap-2 text-sm">
                                        <Badge variant="outline" className="text-xs border-[#2a2a4a] text-gray-400">
                                          {member.role}
                                        </Badge>
                                        {member.worker_code && (
                                          <span className="text-gray-500 text-xs">{member.worker_code}</span>
                                        )}
                                        {member.rating > 0 && (
                                          <span className="flex items-center gap-1 text-yellow-400 text-xs">
                                            <Star className="w-3 h-3 fill-yellow-400" />
                                            {member.rating.toFixed(1)}
                                          </span>
                                        )}
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-right">
                                    {member.has_signed ? (
                                      <div className="flex items-center gap-2 text-green-400">
                                        <CheckCircle className="w-5 h-5" />
                                        <div>
                                          <p className="font-medium text-sm">{t('projectDetail.signed') || 'Signert'}</p>
                                          {member.signed_at && (
                                            <p className="text-xs text-gray-500">
                                              {new Date(member.signed_at).toLocaleDateString('no-NO')}
                                            </p>
                                          )}
                                        </div>
                                      </div>
                                    ) : (
                                      <div className="flex items-center gap-2 text-yellow-400">
                                        <Clock className="w-5 h-5" />
                                        <span className="font-medium text-sm">{t('projectDetail.pending') || 'Venter'}</span>
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Progress Bar */}
                          <div className="mt-4 pt-4 border-t border-[#2a2a4a]">
                            <div className="flex justify-between text-sm mb-2">
                              <span className="text-gray-400">{t('projectDetail.signatureProgress') || 'Signerings-fremgang'}</span>
                              <span className="text-white font-medium">
                                {Math.round((teamContractStatus.signed_count / teamContractStatus.total_members) * 100)}%
                              </span>
                            </div>
                            <div className="w-full h-2 bg-[#0f0f1a] rounded-full overflow-hidden">
                              <div 
                                className={`h-full transition-all duration-500 ${
                                  teamContractStatus.all_members_signed ? 'bg-green-500' : 'bg-purple-500'
                                }`}
                                style={{ width: `${(teamContractStatus.signed_count / teamContractStatus.total_members) * 100}%` }}
                              />
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}
                  </>
                )}
              </TabsContent>

              {/* Applications Tab */}
              <TabsContent value="applications" className="space-y-6 mt-0">
            {/* Applications */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-400" />
                  {t('projectDetail.applications')} ({applications.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!hasApplications ? (
                  <div className="text-center py-8">
                    <Users className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                    <p className="text-gray-400">{t('projectDetail.noApplications')}</p>
                    <p className="text-gray-500 text-sm mt-2">
                      {t('projectDetail.noApplicationsDesc')}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {applications.map((app) => {
                      const isSelected = app.status === "selected";
                      const showContactInfo = isSelected && ["team_selected", "in_progress", "completed", "contract_signed"].includes(project.status);
                      
                      return (
                      <div 
                        key={app.application_id} 
                        className={`p-4 rounded-lg border ${
                          isSelected 
                            ? "bg-green-500/10 border-green-500/30" 
                            : "bg-[#0f0f1a] border-[#2a2a4a]"
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {/* Team Leader Header */}
                            <div className="flex items-center gap-3 mb-4">
                              <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                                <User className="w-5 h-5 text-purple-400" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <p className="text-white font-medium">{app.team_leader_name || "Team Leader"}</p>
                                  {app.team_leader_code && (
                                    <Badge className="bg-purple-500/20 text-purple-400 border-0 text-xs">
                                      {app.team_leader_code}
                                    </Badge>
                                  )}
                                  {app.team_leader_rating > 0 && (
                                    <div className="flex items-center gap-1 text-yellow-400">
                                      <Star className="w-4 h-4 fill-yellow-400" />
                                      <span className="text-sm">{app.team_leader_rating.toFixed(1)}</span>
                                    </div>
                                  )}
                                </div>
                                <p className="text-gray-500 text-sm">
                                  {t('projects.teamLeader')} • Søkt {formatDate(app.applied_at)}
                                </p>
                                {/* Show TL contact info after team selection */}
                                {showContactInfo && (
                                  <div className="flex flex-wrap gap-3 mt-1">
                                    {app.team_leader_email && (
                                      <a href={`mailto:${app.team_leader_email}`} className="flex items-center gap-1 text-[#23F7DD] text-sm hover:underline">
                                        <Mail className="w-3 h-3" />
                                        {app.team_leader_email}
                                      </a>
                                    )}
                                    {app.team_leader_phone && (
                                      <a href={`tel:${app.team_leader_phone}`} className="flex items-center gap-1 text-[#23F7DD] text-sm hover:underline">
                                        <Phone className="w-3 h-3" />
                                        {app.team_leader_phone}
                                      </a>
                                    )}
                                  </div>
                                )}
                              </div>
                              {app.has_warning && (
                                <Badge className="bg-yellow-500/20 text-yellow-400 border-0">
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Advarsel
                                </Badge>
                              )}
                            </div>
                            
                            {app.message && (
                              <div className="mt-3 p-3 bg-[#1a1a2e] rounded-lg">
                                <p className="text-gray-400 text-sm flex items-start gap-2">
                                  <MessageSquare className="w-4 h-4 mt-0.5 shrink-0" />
                                  {app.message}
                                </p>
                              </div>
                            )}
                            
                            {/* Team Members Section */}
                            {app.team_members?.length > 0 && (
                              <div className="mt-4">
                                <p className="text-gray-400 text-sm mb-3 flex items-center gap-2">
                                  <Users className="w-4 h-4" />
                                  {t('projects.workers')} ({app.team_members.length})
                                </p>
                                <div className="grid gap-2">
                                  {app.team_members.map((member, idx) => (
                                    <div 
                                      key={idx}
                                      className="flex items-center justify-between p-3 bg-[#1a1a2e] rounded-lg border border-[#2a2a4a]"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center">
                                          <User className="w-4 h-4 text-blue-400" />
                                        </div>
                                        <div>
                                          <div className="flex items-center gap-2">
                                            <p className="text-white text-sm font-medium">{member.name}</p>
                                            {member.worker_code && (
                                              <Badge className="bg-blue-500/20 text-blue-400 border-0 text-xs">
                                                {member.worker_code}
                                              </Badge>
                                            )}
                                          </div>
                                          <p className="text-gray-500 text-xs">{member.role}</p>
                                          {/* Show contact info after team selection */}
                                          {showContactInfo && (member.email || member.phone) && (
                                            <div className="flex flex-wrap gap-2 mt-1">
                                              {member.email && (
                                                <a href={`mailto:${member.email}`} className="flex items-center gap-1 text-[#23F7DD] text-xs hover:underline">
                                                  <Mail className="w-3 h-3" />
                                                  {member.email}
                                                </a>
                                              )}
                                              {member.phone && (
                                                <a href={`tel:${member.phone}`} className="flex items-center gap-1 text-[#23F7DD] text-xs hover:underline">
                                                  <Phone className="w-3 h-3" />
                                                  {member.phone}
                                                </a>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </div>
                                      {/* Rating Stars */}
                                      <div className="flex items-center gap-1">
                                        {member.rating > 0 ? (
                                          <div className="flex items-center gap-1 text-yellow-400">
                                            <Star className="w-4 h-4 fill-yellow-400" />
                                            <span className="text-sm">{member.rating.toFixed(1)}</span>
                                          </div>
                                        ) : (
                                          <span className="text-gray-500 text-xs">{t('common.noRating')}</span>
                                        )}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                          
                          <div className="flex items-center gap-2 ml-4">
                            {isSelected ? (
                              <Badge className="bg-green-500/20 text-green-400 border-0">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Valgt
                              </Badge>
                            ) : app.status === "rejected" ? (
                              <Badge className="bg-red-500/20 text-red-400 border-0">
                                Avslått
                              </Badge>
                            ) : canSelectTeam ? (
                              <Button
                                size="sm"
                                className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
                                onClick={() => {
                                  setSelectedApp(app);
                                  setShowSelectDialog(true);
                                }}
                              >
                                Velg Team
                              </Button>
                            ) : (
                              <Badge className="bg-gray-500/20 text-gray-400 border-0">
                                Venter
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                    )})}
                  </div>
                )}
              </CardContent>
            </Card>
              </TabsContent>

              {/* Daily Progress Tab */}
              <TabsContent value="progress" className="space-y-6 mt-0">
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <TrendingUp className="w-5 h-5 text-green-400" />
                      {t('projectDetail.dailyProgress')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {dailyProgress.length === 0 ? (
                      <div className="text-center py-8">
                        <TrendingUp className="w-12 h-12 mx-auto text-gray-600 mb-4" />
                        <p className="text-gray-400">{t('projectDetail.noProgressReports')}</p>
                        <p className="text-gray-500 text-sm mt-2">
                          {t('projectDetail.noProgressReportsDesc')}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {dailyProgress.map((progress, idx) => (
                          <div key={idx} className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="text-white font-medium">{progress.title || t('projectDetail.dailyUpdate')}</p>
                                <p className="text-gray-500 text-sm">{formatDate(progress.date)}</p>
                              </div>
                              <Badge className="bg-blue-500/20 text-blue-400 border-0">
                                {t('projectDetail.day')} {progress.day_number || idx + 1}
                              </Badge>
                            </div>
                            <p className="text-gray-300 text-sm">{progress.description}</p>
                            {progress.photos && progress.photos.length > 0 && (
                              <div className="mt-3 grid grid-cols-3 gap-2">
                                {progress.photos.map((photo, i) => (
                                  <img key={i} src={photo} alt="Progress" className="w-full h-24 object-cover rounded" />
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Chat Team Tab (renamed from Contact) */}
              <TabsContent value="contact" className="space-y-6 mt-0">
                <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-indigo-400" />
                      {t('projectDetail.chatTeam') || 'Chat Team'}
                    </CardTitle>
                    <p className="text-gray-400 text-sm mt-1">
                      {t('projectDetail.chatTeamDesc') || 'Chat med klient, holding, team leader og arbeidere'}
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {/* Participants list */}
                    <div className="flex flex-wrap gap-2 pb-3 border-b border-[#2a2a4a]">
                      <Badge className="bg-[#23F7DD]/20 text-[#23F7DD]">
                        <User className="w-3 h-3 mr-1" />
                        {t('common.client') || 'Klient'}
                      </Badge>
                      <Badge className="bg-purple-500/20 text-purple-400">
                        <Building2 className="w-3 h-3 mr-1" />
                        {t('common.holding') || 'Holding'}
                      </Badge>
                      <Badge className="bg-orange-500/20 text-orange-400">
                        <User className="w-3 h-3 mr-1" />
                        {t('common.teamLeader') || 'Basleder'}
                      </Badge>
                      <Badge className="bg-blue-500/20 text-blue-400">
                        <Users className="w-3 h-3 mr-1" />
                        {t('common.workers') || 'Arbeidere'}
                      </Badge>
                    </div>

                    {/* Team Messages */}
                    <div 
                      ref={teamChatContainerRef}
                      className="h-80 overflow-y-auto space-y-3 p-3 bg-[#0f0f1a] rounded-lg"
                    >
                      {teamMessages.length === 0 ? (
                        <div className="h-full flex items-center justify-center text-center">
                          <div>
                            <MessageSquare className="w-10 h-10 mx-auto text-gray-600 mb-2" />
                            <p className="text-gray-500 text-sm">{t('projectDetail.noMessages')}</p>
                            <p className="text-gray-600 text-xs mt-1">{t('projectDetail.startConversation')}</p>
                          </div>
                        </div>
                      ) : (
                        <>
                          {teamMessages.map((msg, idx) => {
                            // Determine message style based on sender type
                            const isOwnMessage = msg.sender_id === user?.user_id || msg.sender_id === user?.email;
                            const senderColors = {
                              'client': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                              'client_admin': 'bg-[#23F7DD]/20 border-[#23F7DD]/30',
                              'holding_admin': 'bg-purple-500/20 border-purple-500/30',
                              'platform_admin': 'bg-purple-500/20 border-purple-500/30',
                              'team_leader': 'bg-orange-500/20 border-orange-500/30',
                              'worker': 'bg-blue-500/20 border-blue-500/30'
                            };
                            const textColors = {
                              'client': 'text-[#23F7DD]',
                              'client_admin': 'text-[#23F7DD]',
                              'holding_admin': 'text-purple-400',
                              'platform_admin': 'text-purple-400',
                              'team_leader': 'text-orange-400',
                              'worker': 'text-blue-400'
                            };
                            const roleLabels = {
                              'client': t('common.client') || 'Klient',
                              'client_admin': t('common.client') || 'Klient',
                              'holding_admin': t('common.holding') || 'Holding',
                              'platform_admin': t('common.admin') || 'Admin',
                              'team_leader': t('common.teamLeader') || 'Basleder',
                              'worker': t('common.worker') || 'Arbeider'
                            };
                            const bgColor = senderColors[msg.sender_type] || 'bg-gray-500/20 border-gray-500/30';
                            const txtColor = textColors[msg.sender_type] || 'text-gray-400';
                            const roleLabel = roleLabels[msg.sender_type] || msg.sender_type;
                            
                            return (
                              <div 
                                key={idx} 
                                className={`flex ${isOwnMessage ? "justify-end" : "justify-start"}`}
                              >
                                <div className={`max-w-[80%] p-3 rounded-lg border ${bgColor}`}>
                                  <div className="flex items-center gap-2 mb-1">
                                    <span className={`text-xs font-medium ${txtColor}`}>
                                      {isOwnMessage ? t('common.you') : msg.sender_name}
                                    </span>
                                    <Badge variant="outline" className={`text-[10px] px-1 py-0 ${txtColor} border-current`}>
                                      {roleLabel}
                                    </Badge>
                                  </div>
                                  <p className="text-white text-sm">{msg.message}</p>
                                  <p className="text-xs opacity-50 mt-1">
                                    {new Date(msg.sent_at || msg.timestamp).toLocaleString("no-NO", {
                                      day: "2-digit",
                                      month: "short",
                                      hour: "2-digit",
                                      minute: "2-digit"
                                    })}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                          <div ref={teamMessagesEndRef} />
                        </>
                      )}
                    </div>

                    {/* Team Message Input */}
                    <form onSubmit={handleSendTeamMessage} className="flex gap-2">
                      <Textarea
                        placeholder={t('projectDetail.writeMessageToTeam') || 'Skriv en melding til teamet...'}
                        value={newTeamMessage}
                        onChange={(e) => setNewTeamMessage(e.target.value)}
                        className="bg-[#0f0f1a] border-[#2a2a4a] text-white resize-none"
                        rows={2}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSendTeamMessage(e);
                          }
                        }}
                      />
                      <Button 
                        type="submit"
                        disabled={sendingTeamMessage || !newTeamMessage.trim()}
                        className="bg-indigo-600 hover:bg-indigo-700 text-white px-4"
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    </form>
                  </CardContent>
                </Card>
              </TabsContent>
            </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Budget Card */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <DollarSign className="w-5 h-5 text-[#23F7DD]" />
                  {t('projectDetail.budget')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex justify-between items-center">
                  <span className="text-gray-400">{t('projectDetail.yourBudget')}</span>
                  <span className="text-white font-bold text-xl">
                    {formatCurrency(project.final_budget || project.proposed_budget)}
                  </span>
                </div>
                {project.application_deadline && (
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-500">{t('projectDetail.applicationDeadline')}</span>
                    <span className="text-gray-400">{formatDate(project.application_deadline)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Card */}
            <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
              <CardHeader>
                <CardTitle className="text-white">{t('projectDetail.status')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2 text-sm">
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${project.status !== "pending_holding_review" ? "text-green-400" : "text-gray-600"}`} />
                    <span className={project.status !== "pending_holding_review" ? "text-white" : "text-gray-500"}>
                      {t('projectDetail.approvedByHolding')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${project.client_signed ? "text-green-400" : "text-gray-600"}`} />
                    <span className={project.client_signed ? "text-white" : "text-gray-500"}>
                      {t('projectDetail.contractSigned')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${hasApplications ? "text-green-400" : "text-gray-600"}`} />
                    <span className={hasApplications ? "text-white" : "text-gray-500"}>
                      {t('projectDetail.applicationsReceived')}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CheckCircle className={`w-4 h-4 ${project.status === "team_selected" || project.status === "in_progress" ? "text-green-400" : "text-gray-600"}`} />
                    <span className={project.status === "team_selected" || project.status === "in_progress" ? "text-white" : "text-gray-500"}>
                      {t('projectDetail.teamSelected')}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* End Project Button - Show only when in_progress or team_contracted */}
            {["in_progress", "team_contracted", "team_selected"].includes(project.status) && teamContractStatus?.all_members_signed && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Flag className="w-5 h-5 text-orange-400" />
                    {t('projectDetail.endProject') || 'Avslutt Prosjekt'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <p className="text-gray-400 text-sm">
                    {t('projectDetail.endProjectDesc') || 'Marker prosjektet som ferdig. Teamet vil motta invitasjon til å signere ferdigstillelsen.'}
                  </p>
                  <Button 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                    onClick={() => setShowEndProjectModal(true)}
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    {t('projectDetail.endProject') || 'Avslutt Prosjekt'}
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Completion Status Card - Show when pending_completion or completed */}
            {(project.status === "pending_completion" || project.status === "completed") && completionStatus && (
              <Card className="bg-[#1a1a2e] border-[#2a2a4a]">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <CheckCircle className={`w-5 h-5 ${completionStatus.all_signed ? 'text-green-400' : 'text-orange-400'}`} />
                    {t('projectDetail.completionStatus') || 'Ferdigstillelse Status'}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Progress */}
                  <div>
                    <div className="flex justify-between text-sm mb-2">
                      <span className="text-gray-400">{t('projectDetail.signatures') || 'Signaturer'}</span>
                      <span className={completionStatus.all_signed ? 'text-green-400' : 'text-orange-400'}>
                        {completionStatus.signed_count}/{completionStatus.total_signatures}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${completionStatus.all_signed ? 'bg-green-500' : 'bg-orange-500'}`}
                        style={{ width: `${(completionStatus.signed_count / completionStatus.total_signatures) * 100}%` }}
                      />
                    </div>
                  </div>

                  {/* Signatures list */}
                  <div className="space-y-2">
                    {completionStatus.signatures?.map((sig, idx) => (
                      <div key={idx} className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded-lg">
                        <div className="flex items-center gap-2">
                          <User className={`w-4 h-4 ${sig.role === 'team_leader' ? 'text-purple-400' : 'text-blue-400'}`} />
                          <span className="text-white text-sm">{sig.name || (sig.role === 'team_leader' ? 'Team Leader' : 'Arbeider')}</span>
                        </div>
                        {sig.signed ? (
                          <Badge className="bg-green-500/20 text-green-400 text-xs">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            {t('projectDetail.signed')}
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-500/20 text-yellow-400 text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {t('projectDetail.pending')}
                          </Badge>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Payment deadline */}
                  {completionStatus.all_signed && completionStatus.payment_deadline && (
                    <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <CheckCircle className="w-4 h-4" />
                        <span>{t('projectDetail.projectCompleted') || 'Prosjekt ferdigstilt!'}</span>
                      </div>
                      <p className="text-gray-400 text-xs mt-1">
                        {t('projectDetail.paymentDeadline') || 'Utbetaling innen'}: {new Date(completionStatus.payment_deadline).toLocaleDateString('no-NO')}
                      </p>
                    </div>
                  )}

                  {/* Completion files */}
                  {completionStatus.completion_files?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-gray-400 text-sm">{t('projectDetail.completionFiles') || 'Vedlagte filer'}:</p>
                      <div className="space-y-1">
                        {completionStatus.completion_files.map((file, idx) => (
                          <a 
                            key={idx}
                            href={file.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-2 text-[#23F7DD] text-sm hover:underline"
                          >
                            {file.type?.includes('image') ? (
                              <ImageIcon className="w-4 h-4" />
                            ) : (
                              <FileText className="w-4 h-4" />
                            )}
                            {file.name}
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Help Box */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
              <h4 className="text-white font-medium mb-2">{t('projectDetail.needHelp')}</h4>
              <p className="text-gray-400 text-sm">
                {t('projectDetail.helpText')}
              </p>
            </div>
          </div>
        </div>
        </Tabs>
      </div>

      {/* Select Team Dialog */}
      <Dialog open={showSelectDialog} onOpenChange={setShowSelectDialog}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white">
          <DialogHeader>
            <DialogTitle>{t('projectDetail.confirmTeamSelection')}</DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('projectDetail.confirmTeamDesc')}
            </DialogDescription>
          </DialogHeader>
          
          {selectedApp && (
            <div className="py-4">
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                    <User className="w-5 h-5 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-white font-medium">{selectedApp.team_leader_name || t('projects.teamLeader')}</p>
                    {selectedApp.team_members?.length > 0 && (
                      <p className="text-gray-500 text-sm">{selectedApp.team_members.length} {t('projectDetail.teamMembers')}</p>
                    )}
                  </div>
                </div>
                
                {selectedApp.message && (
                  <p className="text-gray-400 text-sm mt-3 italic">&ldquo;{selectedApp.message}&rdquo;</p>
                )}
              </div>
              
              <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 mt-0.5" />
                  {t('projectDetail.teamSelectionWarning')}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowSelectDialog(false)}
              className="text-gray-400"
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleSelectTeam}
              disabled={selecting}
              className="bg-[#23F7DD] hover:bg-[#1de9cf] text-black"
            >
              {selecting ? t('common.loading') : t('projectDetail.confirmSelection')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Signed Contract Modal */}
      <Dialog open={showSignedContractModal} onOpenChange={setShowSignedContractModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-indigo-400" />
              {t('projectDetail.signedContract') || 'Signert Kontrakt'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('projectDetail.viewAllSignatures') || 'Vis alle signaturer fra teamet'}
            </DialogDescription>
          </DialogHeader>
          
          {signedContractData && (
            <div className="space-y-6 py-4">
              {/* Project Info - without budget */}
              <div className="p-4 bg-[#0f0f1a] rounded-lg">
                <h3 className="text-white font-medium mb-2">{signedContractData.project_title}</h3>
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="text-gray-400">
                    {t('projectDetail.status')}: <span className="text-green-400">{t('projectDetail.contractSigned')}</span>
                  </span>
                  <span className="text-gray-400">
                    {t('projectDetail.teamSize')}: <span className="text-white">{signedContractData.total_members} {t('common.members')}</span>
                  </span>
                </div>
              </div>

              {/* Team Leader Contract */}
              {signedContractData.tl_signature && (
                <div className="space-y-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-purple-400" />
                    {t('projectDetail.teamLeaderSignature') || 'Basleder Kontrakt'}
                  </h4>
                  <div className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                          <User className="w-6 h-6 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{signedContractData.tl_signature.name}</p>
                          <p className="text-gray-500 text-sm">{signedContractData.tl_signature.worker_code}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge className="bg-green-500/20 text-green-400">
                          <CheckCircle className="w-3 h-3 mr-1" />
                          {t('projectDetail.signed')}
                        </Badge>
                        <p className="text-gray-500 text-xs mt-1">
                          {signedContractData.tl_signature.signed_at && 
                            new Date(signedContractData.tl_signature.signed_at).toLocaleString('no-NO')}
                        </p>
                      </div>
                    </div>
                    
                    {/* Signature Image */}
                    {signedContractData.tl_signature.signature_url && (
                      <div className="bg-white p-3 rounded-lg mb-4">
                        <img 
                          src={signedContractData.tl_signature.signature_url} 
                          alt="Team Leader Signature"
                          className="max-h-20 mx-auto"
                        />
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="flex gap-2">
                      <Button 
                        className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                        onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/team/${projectId}`, `team-contract-${projectId}.pdf`, 'view')}
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        {t('projectDetail.viewContract') || 'Se Kontrakt'}
                      </Button>
                      <Button 
                        className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                        onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/team/${projectId}`, `team-contract-${projectId}.pdf`, 'download')}
                      >
                        <FileText className="w-4 h-4 mr-2" />
                        {t('common.download') || 'Last ned'} PDF
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Worker Contracts */}
              {signedContractData.worker_signatures && signedContractData.worker_signatures.length > 0 && (
                <div className="space-y-3">
                  <h4 className="text-white font-medium flex items-center gap-2">
                    <Users className="w-4 h-4 text-blue-400" />
                    {t('projectDetail.workerSignatures') || 'Arbeider Kontrakter'}
                  </h4>
                  <div className="space-y-3">
                    {signedContractData.worker_signatures.map((sig, idx) => (
                      <div key={idx} className="p-4 bg-[#0f0f1a] rounded-lg border border-[#2a2a4a]">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                              <User className="w-6 h-6 text-blue-400" />
                            </div>
                            <div>
                              <p className="text-white font-medium">{sig.name}</p>
                              <p className="text-gray-500 text-sm">{sig.worker_code}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <Badge className="bg-green-500/20 text-green-400">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              {t('projectDetail.signed')}
                            </Badge>
                            <p className="text-gray-500 text-xs mt-1">
                              {sig.signed_at && new Date(sig.signed_at).toLocaleString('no-NO')}
                            </p>
                          </div>
                        </div>
                        
                        {/* Signature Image */}
                        {sig.signature_url && (
                          <div className="bg-white p-3 rounded-lg mb-4">
                            <img 
                              src={sig.signature_url} 
                              alt={`${sig.name} Signature`}
                              className="max-h-20 mx-auto"
                            />
                          </div>
                        )}
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2">
                          <Button 
                            className="flex-1 bg-indigo-600 hover:bg-indigo-700"
                            onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/worker/${projectId}/${sig.user_id}`, `worker-contract-${sig.worker_code}.pdf`, 'view')}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            {t('projectDetail.viewContract') || 'Se Kontrakt'}
                          </Button>
                          <Button 
                            className="flex-1 bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                            onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/worker/${projectId}/${sig.user_id}`, `worker-contract-${sig.worker_code}.pdf`, 'download')}
                          >
                            <FileText className="w-4 h-4 mr-2" />
                            {t('common.download') || 'Last ned'} PDF
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Summary */}
              <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                <div className="flex items-center gap-2 text-green-400">
                  <CheckCircle className="w-5 h-5" />
                  <span className="font-medium">
                    {signedContractData.total_signed}/{signedContractData.total_members} {t('projectDetail.membersHaveSigned') || 'medlemmer har signert'}
                  </span>
                </div>
              </div>

              {/* Download All Button */}
              <Button 
                className="w-full bg-[#23F7DD] text-black hover:bg-[#70FFEF]"
                onClick={() => handlePdfAction(`${API_URL}/api/contracts-pdf/team/${projectId}`, `team-contract-${projectId}.pdf`, 'download')}
              >
                <FileText className="w-4 h-4 mr-2" />
                {t('projectDetail.downloadTeamContract') || 'Last ned Team Kontrakt'} (PDF)
              </Button>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={() => setShowSignedContractModal(false)}
              className="bg-gray-600 hover:bg-gray-700"
            >
              {t('common.close') || 'Lukk'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* End Project Modal */}
      <Dialog open={showEndProjectModal} onOpenChange={setShowEndProjectModal}>
        <DialogContent className="bg-[#1a1a2e] border-[#2a2a4a] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="w-5 h-5 text-orange-400" />
              {t('projectDetail.endProject') || 'Avslutt Prosjekt'}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {t('projectDetail.endProjectModalDesc') || 'Marker prosjektet som ferdig. Teamleder og arbeidere vil motta invitasjon til å signere ferdigstillelsen. Utbetaling skjer innen 14 dager etter alle signaturer.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Note/Comment */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                {t('projectDetail.completionNote') || 'Kommentar (valgfritt)'}
              </label>
              <Textarea
                placeholder={t('projectDetail.completionNotePlaceholder') || 'Legg til en kommentar om prosjektets ferdigstillelse...'}
                value={endProjectNote}
                onChange={(e) => setEndProjectNote(e.target.value)}
                className="bg-[#0f0f1a] border-[#2a2a4a] text-white"
                rows={3}
              />
            </div>

            {/* File Upload */}
            <div>
              <label className="text-sm text-gray-400 mb-2 block">
                {t('projectDetail.completionPhotos') || 'Last opp bekreftelsesbilder (valgfritt)'}
              </label>
              <div className="border-2 border-dashed border-[#2a2a4a] rounded-lg p-4">
                <input
                  type="file"
                  id="completion-files"
                  multiple
                  accept="image/*,.pdf"
                  onChange={handleEndProjectFileUpload}
                  className="hidden"
                />
                <label 
                  htmlFor="completion-files"
                  className="flex flex-col items-center cursor-pointer"
                >
                  <Upload className={`w-8 h-8 ${uploadingEndFile ? 'text-gray-500 animate-pulse' : 'text-gray-400'}`} />
                  <span className="text-gray-400 text-sm mt-2">
                    {uploadingEndFile 
                      ? (t('common.uploading') || 'Laster opp...') 
                      : (t('projectDetail.clickToUpload') || 'Klikk for å laste opp bilder eller dokumenter')}
                  </span>
                </label>
              </div>

              {/* Uploaded files preview */}
              {endProjectFiles.length > 0 && (
                <div className="mt-3 space-y-2">
                  {endProjectFiles.map((file, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-[#0f0f1a] rounded-lg">
                      <div className="flex items-center gap-2">
                        {file.type?.includes('image') ? (
                          <img src={file.url} alt={file.name} className="w-10 h-10 object-cover rounded" />
                        ) : (
                          <FileText className="w-6 h-6 text-gray-400" />
                        )}
                        <span className="text-white text-sm truncate max-w-[200px]">{file.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setEndProjectFiles(prev => prev.filter((_, i) => i !== idx))}
                        className="text-red-400 hover:text-red-300"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Info box */}
            <div className="p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5" />
                <div>
                  <p className="text-orange-400 text-sm font-medium">
                    {t('projectDetail.endProjectWarning') || 'Viktig informasjon'}
                  </p>
                  <p className="text-gray-400 text-xs mt-1">
                    {t('projectDetail.endProjectWarningText') || 'Etter at du avslutter prosjektet, vil teamleder og alle arbeidere motta en invitasjon til å signere ferdigstillelsen. Utbetalingen vil skje innen 14 dager etter at alle har signert.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => {
                setShowEndProjectModal(false);
                setEndProjectNote("");
                setEndProjectFiles([]);
              }}
              className="text-gray-400"
            >
              {t('common.cancel') || 'Avbryt'}
            </Button>
            <Button
              onClick={handleEndProject}
              disabled={endingProject}
              className="bg-orange-500 hover:bg-orange-600 text-white"
            >
              {endingProject 
                ? (t('common.loading') || 'Venter...') 
                : (t('projectDetail.confirmEndProject') || 'Bekreft Ferdigstillelse')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
